import { CSSProperties } from 'react';
import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { useCompanies } from '@/models/company';
import { hasPermission } from '@/models/user';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';

export const TransactionDestinationSelect = ({
  setPage,
  transactionDestination,
  setTransactionDestination,
  style,
}: {
  setPage?: (page: number) => void;
  transactionDestination: TransactionDestination;
  setTransactionDestination: (destination: TransactionDestination) => void;
  style?: CSSProperties;
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  const { data, isFetching } = useCompanies({ limit: 100 });

  const companyOptions = (data?.companies ?? []).map((workshop) => ({
    type: 'company' as const,
    label: workshop.name,
    id: workshop.id,
  }));

  const clientOption = {
    type: 'client' as const,
    label: t({ id: 'accounting.transactions.destination.client', message: 'Client' }),
    id: undefined,
  };

  const showClientOption = hasPermission(currentSession, 'view_invoice', {
    organizationId: currentOrganization?.id,
  });

  const options = showClientOption ? [clientOption, ...companyOptions] : companyOptions;

  const selectedOption =
    options.find(
      (option) =>
        option.type === transactionDestination.type && option.id === transactionDestination.id
    ) ?? null;

  if (!options.length) {
    return null;
  }

  return (
    <InputSelect
      variant="select"
      placeholder={t({
        id: 'accounting.transactions.destination.placeholder',
        message: 'Destination: All',
      })}
      aria-label={t({ id: 'accounting.transactions.destination.label', message: 'Destination' })}
      value={selectedOption}
      isClearable
      options={options}
      getOptionValue={(option) => `${option.type}-${option.id}`}
      getOptionLabel={(option) => option.label}
      onChange={(option, action) => {
        if (action.action === 'clear') {
          setTransactionDestination({ type: 'all' });
        } else if (option) {
          setTransactionDestination({ type: option.type, id: option.id });
        }

        setPage?.(1);
      }}
      isLoading={isFetching}
      style={style}
    />
  );
};

export type TransactionDestination = {
  type: 'all' | 'client' | 'company';
  id?: string;
};
